<template>
  <div class="flex items-center gap-1.5 flex-wrap">
    <EntityTypeFilterPill
      v-for="t in pinnedTypes"
      :key="'type-pin-' + t.id"
      :type="t"
      :active="isActive(t)"
      @click="onPillClick(t)"
    />

    <!-- "N more": reads as a filter control (funnel icon, bold label) same as the
         collapsed chip it replaces, but the count is always visible up front. -->
    <button
      v-if="overflowTypes.length > 0"
      type="button"
      class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
      :aria-expanded="expanded"
      :aria-label="expanded ? 'Hide more type filters' : 'Show more type filters'"
      @click="expanded = !expanded"
    >
      <svg
        class="w-3.5 h-3.5 text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M3 4.5h18M6 9h12M10 13.5h4"
        />
      </svg>
      <span class="text-xs font-semibold">{{ overflowTypes.length }} more</span>
      <svg
        class="w-2.5 h-2.5 text-gray-400 transition-transform"
        :class="{ 'rotate-90': expanded }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <!-- Expanded: reuses the same pill component, just for the remaining types. -->
    <div v-if="expanded" class="basis-full flex flex-wrap gap-1.5 mt-1.5">
      <EntityTypeFilterPill
        v-for="t in overflowTypes"
        :key="'type-more-' + t.id"
        :type="t"
        :active="isActive(t)"
        @click="onPillClick(t)"
      />
    </div>
  </div>
</template>

<script>
import { getPinnedTypes } from '@/utils/entityTypeFilters'
import EntityTypeFilterPill from './EntityTypeFilterPill.vue'

export default {
  name: 'EntityTypeFilters',
  components: { EntityTypeFilterPill },
  props: {
    types: { type: Array, required: true },
    activeType: { type: String, default: null },
  },
  emits: ['select', 'clear'],
  data() {
    return { expanded: false }
  },
  computed: {
    pinnedTypes() {
      return getPinnedTypes(this.types)
    },
    overflowTypes() {
      const pinnedIds = new Set(this.pinnedTypes.map((t) => t.id))
      return this.types.filter((t) => !pinnedIds.has(t.id))
    },
  },
  watch: {
    // If the active filter is one of the overflow types, expand so the row's
    // own active-pill styling is actually visible instead of hiding behind
    // "N more" with nothing in the collapsed row showing what's selected.
    activeType: {
      immediate: true,
      handler(val) {
        if (val && this.overflowTypes.some((t) => t.type_name === val)) {
          this.expanded = true
        }
      },
    },
  },
  methods: {
    isActive(t) {
      return this.activeType === t.type_name
    },
    onPillClick(t) {
      if (this.isActive(t)) {
        this.$emit('clear')
      } else {
        this.$emit('select', t.type_name)
      }
    },
  },
}
</script>
